import { useState } from 'react'
import { Stack } from '@mui/material'
import { QueryKey } from '@tanstack/react-query'
import { OverlayState } from '@/hooks/useOverlay'
import { useUpdateVendorSettings } from '@/services/hooks/relay/dynamic'
import { OrgCredentialType } from '@/types/credentials'
import { BasePaymentMethod } from '@/types/relay'
import { VendorSettings } from '@/types/vendor-settings'
import {
  GL_ACCOUNT_INFO_TYPES_IDS,
  PAYMENT_METHOD_INFO_TYPES_IDS,
} from '@/utils/info-types'
import { Dialog, DialogContent } from '@/components/dialog'
import { useCredentialTypeContext } from '@/components/integration-settings/CredentialTypeProvider'
import LiveListPicker from '@/components/integration-settings/LiveListPicker'
import useBootstrappedLiveListSelection from '@/components/integration-settings/useBootstrappedLiveListSelection'
import VendorRollUpLineItemsSwitch from '@/components/integration-settings/VendorRollUpLineItemsSwitch'

type EditVendorSettingsDialogProps = {
  overlay: OverlayState
  allowedConfiguration: OrgCredentialType['allowed_configuration']
  vendorSettings: VendorSettings
  queryKey?: QueryKey
}

export default function EditVendorSettingsDialog({
  overlay,
  allowedConfiguration,
  vendorSettings,
  queryKey,
}: EditVendorSettingsDialogProps) {
  const [paymentMethodSearchValue, setPaymentMethodSearchValue] = useState('')
  const [accountSearchValue, setAccountSearchValue] = useState('')

  const credentialType = useCredentialTypeContext()
  const orgId = vendorSettings.org_id

  // Reset bootstrap when dialog context changes to a different vendor/credential.
  const listResetKey = `${orgId}:${credentialType.id}:${vendorSettings.native_vendor_id}`

  const paymentMethodSelection =
    useBootstrappedLiveListSelection<BasePaymentMethod>({
      infoTypeId: PAYMENT_METHOD_INFO_TYPES_IDS[0],
      orgId,
      orgCredentialTypeId: credentialType.id,
      enabled: allowedConfiguration.includes('vendor_payment'),
      currentId: vendorSettings.payment_method_id,
      searchValue: paymentMethodSearchValue,
      limit: '100',
      resetKey: `${listResetKey}:payment`,
    })

  const accountSelection = useBootstrappedLiveListSelection({
    infoTypeId: GL_ACCOUNT_INFO_TYPES_IDS[0],
    orgId,
    orgCredentialTypeId: credentialType.id,
    enabled: allowedConfiguration.includes('vendor_gl'),
    currentId: vendorSettings.default_gl_id,
    searchValue: accountSearchValue,
    limit: '100',
    resetKey: `${listResetKey}:account`,
  })

  const { updateVendorSettings } = useUpdateVendorSettings({
    orgId,
    vendorId: vendorSettings.native_vendor_id,
    orgCredentialTypeId: credentialType.id,
    detailQueryKey: queryKey,
  })

  function handleChange(updatedValues: Partial<VendorSettings>) {
    if (vendorSettings) {
      updateVendorSettings({
        ...vendorSettings,
        ...updatedValues,
        id: vendorSettings.native_vendor_id, // for TS to be happy
      })
    }
  }

  return (
    <Dialog title="Edit Vendor Settings" {...overlay} maxWidth="sm">
      <DialogContent>
        <Stack spacing={3}>
          {allowedConfiguration.includes('vendor_payment') && (
            <LiveListPicker
              value={paymentMethodSelection.selectedOption}
              options={paymentMethodSelection.options}
              onChange={(entity) => {
                handleChange({
                  payment_method_id: entity?.entity_id || null,
                })
              }}
              searchValue={paymentMethodSearchValue}
              setSearchValue={setPaymentMethodSearchValue}
              hasNextPage={paymentMethodSelection.hasNextPage}
              fetchNextPage={paymentMethodSelection.fetchNextPage}
              label="Payment Method"
              isLoading={paymentMethodSelection.isLoading}
              isFetching={paymentMethodSelection.isFetching}
              disableClearable={false}
            />
          )}
          {allowedConfiguration.includes('vendor_gl') && (
            <LiveListPicker
              value={accountSelection.selectedOption}
              options={accountSelection.options}
              onChange={(entity) => {
                handleChange({
                  default_gl_id: entity?.entity_id || null,
                })
              }}
              searchValue={accountSearchValue}
              setSearchValue={setAccountSearchValue}
              hasNextPage={accountSelection.hasNextPage}
              fetchNextPage={accountSelection.fetchNextPage}
              label="Default GL Account"
              isLoading={accountSelection.isLoading}
              isFetching={accountSelection.isFetching}
              disableClearable={false}
            />
          )}
          {allowedConfiguration.includes('vendor_roll_up') && (
            <VendorRollUpLineItemsSwitch
              onChange={(checked) => {
                handleChange({
                  roll_up_line_items: checked,
                })
              }}
              checked={vendorSettings.roll_up_line_items}
            />
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  )
}
